import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../Layout/Sidebar";
import Header from "../Layout/Header";
import Swal from "sweetalert2";
import axios from "axios";
import "./TenantSubscription.css";
import { API_BASE } from "../../../ApiUrl";
import SubscriptionTabs from "./SubscriptionTabs";

const TENANT_RECORDS_API_URL = `${API_BASE}/tenant-records/`;
const SUBSCRIPTIONS_API_URL = `${API_BASE}/tenant-subscriptions/`;

const PLAN_LABELS = {
  PILOT: "Pilot",
  STANDARD: "Standard",
  ENTERPRISE: "Enterprise"
};

const STATUS_LABELS = {
  TRIAL: "Trial",
  ACTIVE: "Active",
  PAST_DUE: "Past Due",
  SUSPENDED: "Suspended",
  CANCELLED: "Cancelled",
  EXPIRED: "Expired"
};

const TenantSubscription = () => {
  const navigate = useNavigate();

  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const getTenantUser = () => {
    try {
      const tenantUser = localStorage.getItem("tenant_user");
      return tenantUser ? JSON.parse(tenantUser) : null;
    } catch (error) {
      console.error("Error reading tenant_user:", error);
      return null;
    }
  };

  useEffect(() => {
    const tenantUser = getTenantUser();
    if (tenantUser?.tenant_id) fetchSubscriptions(tenantUser.tenant_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSubscriptions = async (tenantId) => {
    try {
      setLoading(true);

      // Try tenant-records first; fall back to direct endpoint
      let rows = [];

      try {
        const response = await axios.post(
          TENANT_RECORDS_API_URL,
          {
            tenant_id: tenantId,
            tables: ["tenant_subscriptions"]
          },
          { headers: { "Content-Type": "application/json" } }
        );

        const result = response.data;
        if (result?.data?.tenant_subscriptions) {
          rows = result.data.tenant_subscriptions;
        } else if (result?.tenant_subscriptions) {
          rows = result.tenant_subscriptions;
        } else if (Array.isArray(result?.data)) {
          rows = result.data;
        } else if (Array.isArray(result)) {
          rows = result;
        }
      } catch {
        const response = await axios.get(SUBSCRIPTIONS_API_URL, {
          params: { tenant: tenantId }
        });
        const result = response.data;
        rows = result?.data
          ? result.data
          : Array.isArray(result)
          ? result
          : result?.results || [];
      }

      // Sort newest first (by created_at, then id)
      rows.sort((a, b) => {
        const aT = a.created_at ? new Date(a.created_at).getTime() : 0;
        const bT = b.created_at ? new Date(b.created_at).getTime() : 0;
        if (aT !== bT) return bT - aT;
        return (b.id || 0) - (a.id || 0);
      });

      setSubscriptions(rows);
    } catch (error) {
      console.error("Error fetching tenant subscriptions:", error);
      Swal.fire({
        icon: "error",
        title: "Unable to Load Subscription",
        text: "Could not fetch tenant subscriptions."
      });
    } finally {
      setLoading(false);
    }
  };

  const currentPlan = subscriptions.length ? subscriptions[0] : null;
  const history = subscriptions.slice(1);

  const planLabel = (v) => PLAN_LABELS[v] || v || "-";
  const statusLabel = (v) => STATUS_LABELS[v] || v || "-";

  const formatDate = (v) => {
    if (!v) return "-";
    return String(v).substring(0, 10);
  };

  const formatDateTime = (v) => {
    if (!v) return "-";
    try {
      const d = new Date(v);
      return d.toLocaleString();
    } catch {
      return String(v);
    }
  };

  const usagePercent = (used, limit) => {
    const u = Number(used) || 0;
    const l = Number(limit) || 0;
    if (!l) return 0;
    return Math.min(100, Math.round((u / l) * 100));
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "ACTIVE":
        return "subscription-status subscription-status--active";
      case "TRIAL":
        return "subscription-status subscription-status--trial";
      case "PAST_DUE":
        return "subscription-status subscription-status--pastdue";
      case "SUSPENDED":
        return "subscription-status subscription-status--suspended";
      case "CANCELLED":
        return "subscription-status subscription-status--cancelled";
      case "EXPIRED":
        return "subscription-status subscription-status--expired";
      default:
        return "subscription-status";
    }
  };

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Header />

        <div className="tenant-subscription-page">
          <SubscriptionTabs />

          <div className="tenant-subscription-page-header">
            <div>
              <h2>Subscription</h2>
            </div>
          </div>

          {loading ? (
            <div className="tenant-subscription-loading">
              Loading subscription...
            </div>
          ) : !currentPlan ? (
            <div className="tenant-subscription-empty">
              No subscription found for this tenant.
            </div>
          ) : (
            <>
              {/* ==================================================
                  CURRENT PLAN
              ================================================== */}

              <div className="subscription-current-card">
                <div className="subscription-current-header">
                  <div>
                    <span className="subscription-current-label">
                      Current Plan
                    </span>
                    <h3 className="subscription-current-plan">
                      {planLabel(currentPlan.plan)}
                    </h3>
                  </div>

                  <span className={getStatusClass(currentPlan.status)}>
                    {statusLabel(currentPlan.status)}
                  </span>
                </div>

                <div className="subscription-current-grid">
                  <div className="subscription-current-field">
                    <span className="subscription-current-field-label">
                      Start Date
                    </span>
                    <strong className="subscription-current-field-value">
                      {formatDate(currentPlan.start_date)}
                    </strong>
                  </div>

                  <div className="subscription-current-field">
                    <span className="subscription-current-field-label">
                      Renewal Date
                    </span>
                    <strong className="subscription-current-field-value">
                      {formatDate(currentPlan.renewal_date)}
                    </strong>
                  </div>

                  <div className="subscription-current-field">
                    <span className="subscription-current-field-label">
                      Expiry Date
                    </span>
                    <strong className="subscription-current-field-value">
                      {formatDate(currentPlan.expiry_date)}
                    </strong>
                  </div>

                  <div className="subscription-current-field">
                    <span className="subscription-current-field-label">
                      Last Updated
                    </span>
                    <strong className="subscription-current-field-value">
                      {formatDateTime(currentPlan.updated_at)}
                    </strong>
                  </div>
                </div>
              </div>

              {/* ==================================================
                  USAGE
              ================================================== */}

              <div className="subscription-usage-card">
                <div className="subscription-usage-header">
                  <h3>Usage</h3>
                </div>

                <div className="subscription-usage-grid">
                  <div className="subscription-usage-item">
                    <div className="subscription-usage-row">
                      <span className="subscription-usage-label">Seats</span>
                      <span className="subscription-usage-numbers">
                        {Number(currentPlan.used_seats) || 0} /{" "}
                        {Number(currentPlan.seat_limit) || 0}
                      </span>
                    </div>
                    <div className="subscription-usage-bar">
                      <div
                        className="subscription-usage-bar-fill"
                        style={{
                          width: `${usagePercent(
                            currentPlan.used_seats,
                            currentPlan.seat_limit
                          )}%`
                        }}
                      />
                    </div>
                    <span className="subscription-usage-percent">
                      {usagePercent(
                        currentPlan.used_seats,
                        currentPlan.seat_limit
                      )}
                      % used
                    </span>
                  </div>

                  <div className="subscription-usage-item">
                    <div className="subscription-usage-row">
                      <span className="subscription-usage-label">
                        Projects
                      </span>
                      <span className="subscription-usage-numbers">
                        {Number(currentPlan.used_projects) || 0} /{" "}
                        {currentPlan.project_limit != null
                          ? Number(currentPlan.project_limit)
                          : "∞"}
                      </span>
                    </div>
                    <div className="subscription-usage-bar">
                      <div
                        className="subscription-usage-bar-fill"
                        style={{
                          width:
                            currentPlan.project_limit == null
                              ? "0%"
                              : `${usagePercent(
                                  currentPlan.used_projects,
                                  currentPlan.project_limit
                                )}%`
                        }}
                      />
                    </div>
                    <span className="subscription-usage-percent">
                      {currentPlan.project_limit == null
                        ? "Unlimited"
                        : `${usagePercent(
                            currentPlan.used_projects,
                            currentPlan.project_limit
                          )}% used`}
                    </span>
                  </div>

                  <div className="subscription-usage-item">
                    <div className="subscription-usage-row">
                      <span className="subscription-usage-label">
                        Storage
                      </span>
                      <span className="subscription-usage-numbers">
                        {Number(currentPlan.used_storage_gb) || 0} GB /{" "}
                        {currentPlan.storage_limit_gb != null
                          ? `${Number(currentPlan.storage_limit_gb)} GB`
                          : "∞"}
                      </span>
                    </div>
                    <div className="subscription-usage-bar">
                      <div
                        className="subscription-usage-bar-fill"
                        style={{
                          width:
                            currentPlan.storage_limit_gb == null
                              ? "0%"
                              : `${usagePercent(
                                  currentPlan.used_storage_gb,
                                  currentPlan.storage_limit_gb
                                )}%`
                        }}
                      />
                    </div>
                    <span className="subscription-usage-percent">
                      {currentPlan.storage_limit_gb == null
                        ? "Unlimited"
                        : `${usagePercent(
                            currentPlan.used_storage_gb,
                            currentPlan.storage_limit_gb
                          )}% used`}
                    </span>
                  </div>
                </div>
              </div>

              {/* ==================================================
                  HISTORY
              ================================================== */}

              <div className="subscription-history-card">
                <div className="subscription-history-header">
                  <h3>Subscription History</h3>
                </div>

                {history.length === 0 ? (
                  <div className="subscription-history-empty">
                    No previous subscriptions.
                  </div>
                ) : (
                  <div className="subscription-history-table-wrapper">
                    <table className="subscription-history-table">
                      <thead>
                        <tr>
                          <th>Plan</th>
                          <th>Status</th>
                          <th>Start Date</th>
                          <th>Renewal Date</th>
                          <th>Expiry Date</th>
                          <th>Seat Limit</th>
                          <th>Project Limit</th>
                          <th>Storage (GB)</th>
                          <th>Updated</th>
                        </tr>
                      </thead>

                      <tbody>
                        {history.map((s) => (
                          <tr key={s.id}>
                            <td>
                              <span className="subscription-plan-pill">
                                {planLabel(s.plan)}
                              </span>
                            </td>

                            <td>
                              <span className={getStatusClass(s.status)}>
                                {statusLabel(s.status)}
                              </span>
                            </td>

                            <td>{formatDate(s.start_date)}</td>
                            <td>{formatDate(s.renewal_date)}</td>
                            <td>{formatDate(s.expiry_date)}</td>
                            <td>{s.seat_limit ?? "-"}</td>
                            <td>{s.project_limit ?? "-"}</td>
                            <td>{s.storage_limit_gb ?? "-"}</td>
                            <td className="subscription-history-muted">
                              {formatDateTime(s.updated_at)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TenantSubscription;